import React, { useEffect, Suspense, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import styled from 'styled-components';
import ProjectsCarousel from './ProjectsCarousel';
import TouchPanControls from './TouchPanControls';
import gsap from 'gsap';
import * as THREE from 'three';
import Wanderer from './Wanderer';
import { PROJECT_PAN_LIMIT } from './projectViewConfig';

const PHONE_BREAKPOINT = '(max-width: 768px)';

const PhoneHint = styled.div`
  position: absolute;
  top: max(20px, env(safe-area-inset-top));
  left: 50%;
  z-index: 12;
  min-height: 40px;
  display: none;
  align-items: center;
  padding: 0 14px;
  transform: translateX(-50%);
  border: 1px solid var(--color-portfolio-wine);
  background: rgba(23, 17, 19, 0.9);
  color: var(--color-portfolio-silver-light);
  font-family: var(--font-portfolio-mono);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  pointer-events: none;
  backdrop-filter: blur(8px);

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MotionButton = styled.button`
  position: absolute;
  left: 20px;
  bottom: max(20px, env(safe-area-inset-bottom));
  z-index: 12;
  min-width: 44px;
  min-height: 44px;
  display: none;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 14px;
  border: 1px solid var(--color-portfolio-wine);
  background: rgba(23, 17, 19, 0.94);
  color: var(--color-portfolio-silver-light);
  font-family: var(--font-portfolio-mono);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  backdrop-filter: blur(8px);

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $enabled }) => ($enabled ? 'var(--color-portfolio-wine)' : 'transparent')};
    border: 1px solid var(--color-portfolio-wine);
  }

  &:focus-visible {
    outline: 3px solid var(--color-portfolio-wine);
    outline-offset: 3px;
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

const usePhoneViewport = () => {
  const [isPhone, setIsPhone] = useState(
    () => window.matchMedia(PHONE_BREAKPOINT).matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(PHONE_BREAKPOINT);
    const handleChange = (event) => setIsPhone(event.matches);

    setIsPhone(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isPhone;
};

// Camera controls for projects section
const CameraController = ({ isPhone, isActive }) => {
  const { camera } = useThree();

  useEffect(() => {
    camera.fov = isPhone ? 52 : 45;
    camera.updateProjectionMatrix();

    if (!isActive) {
      camera.position.set(isPhone ? 0 : 2, isPhone ? 0.2 : 0, isPhone ? 8.2 : 8.5);
      return undefined;
    }

    let cameraTween;
    if (isPhone) {
      cameraTween = gsap.to(camera.position, { z: 8.2, y: 0.2, x: 0, duration: 1 });
    } else {
      cameraTween = gsap.to(camera.position, { z: 8.5, y: 0, x: 2, duration: 1 });
    }

    return () => cameraTween?.kill();
  }, [camera, isActive, isPhone]);

  useFrame((state, delta) => {
    if (!isActive) return;

    if (!isPhone) {
      camera.rotation.y = THREE.MathUtils.clamp(
        THREE.MathUtils.lerp(
          camera.rotation.y,
          -state.pointer.x * PROJECT_PAN_LIMIT,
          0.03
        ),
        -PROJECT_PAN_LIMIT,
        PROJECT_PAN_LIMIT
      );
      camera.position.z = THREE.MathUtils.damp(
        camera.position.z,
        7.5 - state.pointer.y,
        7,
        delta
      );
    }
  });

  return null;
};

// Main 3D Projects component
const Projects3D = ({ isActive = false }) => {
  const isPhone = usePhoneViewport();
  const [motionEnabled, setMotionEnabled] = useState(false);
  const [motionAvailable] = useState(
    () => typeof window.DeviceOrientationEvent !== 'undefined'
  );

  useEffect(() => {
    if (!isActive) setMotionEnabled(false);
  }, [isActive]);

  const toggleMotion = async () => {
    if (motionEnabled) {
      setMotionEnabled(false);
      return;
    }

    const OrientationEvent = window.DeviceOrientationEvent;

    try {
      if (typeof OrientationEvent.requestPermission === 'function') {
        const permission = await OrientationEvent.requestPermission();
        if (permission !== 'granted') return;
      }

      setMotionEnabled(true);
    } catch (error) {
      setMotionEnabled(false);
    }
  };

  return (
    <>
      {isActive && isPhone && (
        <PhoneHint aria-hidden="true">
          {motionEnabled
            ? 'Tilt to explore · tap to expand'
            : 'Tap to expand · drag to explore'}
        </PhoneHint>
      )}
      {isActive && isPhone && motionAvailable && (
        <MotionButton
          type="button"
          $enabled={motionEnabled}
          aria-pressed={motionEnabled}
          onClick={toggleMotion}
        >
          {motionEnabled ? 'Tilt enabled' : 'Enable tilt'}
        </MotionButton>
      )}
      <Canvas
        camera={{
          position: [0, -1, 8.5],
          fov: isPhone ? 52 : 45,
          near: 0.1,
          far: 1000
        }}
        eventSource={isActive ? document.documentElement : undefined}
        eventPrefix="client"
        frameloop={isActive ? 'always' : 'demand'}
        dpr={isPhone
          ? Math.min(Math.max(window.devicePixelRatio || 1, 1.5), 2.25)
          : [1, 1.75]}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: isActive ? 'auto' : 'none',
          touchAction: isActive ? 'pan-y' : 'none',
          zIndex: 5
        }}
      >
        <color attach="background" args={['#171113']} />
        <CameraController isPhone={isPhone} isActive={isActive} />
        <Suspense fallback={null}>
          <group>
            <Wanderer
              rotation={new THREE.Euler(0, Math.PI / 6, 0)}
              scale={new THREE.Vector3(1.5, 1.5, 1.5)}
              position={new THREE.Vector3(0, -1, -1)}
            />
            <ProjectsCarousel isPhone={isPhone} isActive={isActive} />
            {isActive && isPhone && <TouchPanControls motionEnabled={motionEnabled} />}
          </group>
        </Suspense>
      </Canvas>
    </>
  );
};

export default Projects3D;
